import React, { useState, useEffect, useCallback } from 'react';
import { Package, AlertTriangle, CheckCircle, Clock, Eye, Edit3, Plus, Search, Filter, TrendingDown, Calendar, Save, X } from 'lucide-react';
import StockService from '../../services/medication/StockService';
import { useToast } from '../Common/ToastProvider';
import { useConfirm } from '../Common/ConfirmDialog';

const StockManagement = ({ onClose }) => {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockItems, setStockItems] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [addForm, setAddForm] = useState({
    name: '', genericName: '', category: '', quantity: '', minQuantity: '',
    unit: 'tablets', supplier: '', batchNumber: '', expiryDate: '', location: '', unitCost: '',
  });

  const loadStock = useCallback(async () => {
    const result = await StockService.getAll();
    if (result.success) {
      setStockItems(result.data.map((s) => ({
        ...s,
        id: s.id,
        medication: s.name,
        currentStock: s.quantity,
        minStock: s.minQuantity,
        maxStock: (s.minQuantity || 50) * 3,
        batch: s.batchNumber || '',
        expiry: s.expiryDate || '',
        costPerUnit: s.unitCost || 0,
        status: s.quantity <= 0 ? 'Out of Stock' : s.quantity <= (s.minQuantity || 0) ? 'Low Stock' : 'Good',
        daysToExpiry: s.expiryDate ? Math.max(0, Math.round((new Date(s.expiryDate) - new Date()) / 86400000)) : 999,
      })));
    }
  }, []);

  useEffect(() => { loadStock(); }, [loadStock]);

  const handleAddStock = async () => {
    const result = await StockService.addStock({
      name: addForm.name, genericName: addForm.genericName, category: addForm.category,
      quantity: addForm.quantity, minQuantity: addForm.minQuantity, unit: addForm.unit,
      supplier: addForm.supplier, batchNumber: addForm.batchNumber,
      expiryDate: addForm.expiryDate, location: addForm.location, unitCost: parseFloat(addForm.unitCost) || 0,
    });
    if (result.success) {
      setShowAddForm(false);
      setAddForm({ name: '', genericName: '', category: '', quantity: '', minQuantity: '', unit: 'tablets', supplier: '', batchNumber: '', expiryDate: '', location: '', unitCost: '' });
      loadStock();
    } else {
      showToast(result.error || 'Failed to add stock', 'error');
    }
  };

  const handleEditStock = async () => {
    if (!editItem) return;
    const result = await StockService.updateStock(editItem.id, {
      name: editItem.medication || editItem.name,
      quantity: parseInt(editItem.currentStock, 10) || editItem.quantity,
      minQuantity: parseInt(editItem.minStock, 10) || editItem.minQuantity,
      supplier: editItem.supplier,
      batchNumber: editItem.batch || editItem.batchNumber,
      expiryDate: editItem.expiry || editItem.expiryDate,
      location: editItem.location,
      unitCost: parseFloat(editItem.costPerUnit || editItem.unitCost) || 0,
    });
    if (result.success) {
      setShowEditForm(false);
      setEditItem(null);
      loadStock();
    } else {
      showToast(result.error || 'Failed to update', 'error');
    }
  };

  const handleDeleteStock = async (id) => {
    const confirmed = await confirm({ title: 'Delete Stock Item', message: 'Are you sure you want to delete this stock item?', confirmLabel: 'Delete', variant: 'danger' });
    if (!confirmed) return;
    const result = await StockService.deleteStock(id);
    if (result.success) loadStock();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Good': return 'bg-green-100 text-green-800';
      case 'Low Stock': return 'bg-yellow-100 text-yellow-800';
      case 'Out of Stock': return 'bg-red-100 text-red-800';
      case 'Expiring Soon': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getExpiryColor = (daysToExpiry) => {
    if (daysToExpiry < 30) return 'text-red-600';
    if (daysToExpiry < 90) return 'text-orange-600';
    return 'text-green-600';
  };

  const filteredItems = stockItems.filter(item => {
    const matchesSearch = item.medication.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    const matchesLocation = filterLocation === 'all' || item.location.includes(filterLocation);
    return matchesSearch && matchesStatus && matchesLocation;
  });

  const totalValue = stockItems.reduce((sum, item) => sum + (item.currentStock * item.costPerUnit), 0);
  const lowStockCount = stockItems.filter(item => item.status === 'Low Stock' || item.status === 'Out of Stock').length;
  const expiringSoonCount = stockItems.filter(item => item.daysToExpiry < 30).length;

  const handleNewStock = () => {
    setShowAddForm(true);
  };

  const handleViewDetails = (item) => {
    setSelectedStock(item);
  };

  const handleOpenEdit = (item) => {
    setEditItem({ ...item });
    setShowEditForm(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              Stock Management
              <Package className="w-8 h-8 text-blue-200" />
            </h1>
            <p className="mt-2 opacity-90">
              Track medication inventory, expiry dates, and reorder points across all wards
            </p>
          </div>
          <button 
            onClick={handleNewStock}
            className="bg-white text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-opacity-90 transition-all flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Stock
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{stockItems.length}</h3>
              <p className="text-sm text-gray-600">Total Items</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">${totalValue.toFixed(2)}</h3>
              <p className="text-sm text-gray-600">Total Value</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{lowStockCount}</h3>
              <p className="text-sm text-gray-600">Low Stock</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Clock className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{expiringSoonCount}</h3>
              <p className="text-sm text-gray-600">Expiring Soon</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search stock items by medication, ID, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="Good">Good</option>
              <option value="Low Stock">Low Stock</option>
              <option value="Out of Stock">Out of Stock</option>
              <option value="Expiring Soon">Expiring Soon</option>
            </select>
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Locations</option>
              <option value="Ward A">Ward A</option>
              <option value="Ward B">Ward B</option>
              <option value="Ward C">Ward C</option>
            </select>
            <button 
              onClick={() => console.log('Apply filters')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filter
            </button>
          </div>
        </div>

        {/* Stock Items List */}
        <div className="space-y-4">
          {filteredItems.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg">{item.medication}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                    {item.daysToExpiry < 30 && (
                      <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                        Expiring Soon
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Current Stock</p>
                      <p className="text-sm text-gray-600">{item.currentStock} {item.unit}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Min/Max Stock</p>
                      <p className="text-sm text-gray-600">{item.minStock}/{item.maxStock} {item.unit}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Location</p>
                      <p className="text-sm text-gray-600">{item.location}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Expiry Date</p>
                      <p className={`text-sm ${getExpiryColor(item.daysToExpiry)}`}>
                        {item.expiry} ({item.daysToExpiry} days)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                    <span className="flex items-center gap-1">
                      <Package className="w-4 h-4" />
                      Batch: {item.batch}
                    </span>
                    <span className="flex items-center gap-1">
                      <TrendingDown className="w-4 h-4" />
                      Reorder at: {item.reorderPoint} {item.unit}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Last restocked: {item.lastRestocked}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-600">
                      <strong>Cost per unit:</strong> ${item.costPerUnit.toFixed(2)}
                    </span>
                    <span className="text-gray-600">
                      <strong>Total value:</strong> ${(item.currentStock * item.costPerUnit).toFixed(2)}
                    </span>
                    <span className="text-gray-600">
                      <strong>Supplier:</strong> {item.supplier}
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleViewDetails(item)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleOpenEdit(item)}
                    className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                    title="Edit Stock"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stock Details Modal */}
      {selectedStock && (
        <StockDetailsModal
          item={selectedStock}
          onClose={() => setSelectedStock(null)}
          onEdit={() => handleOpenEdit(selectedStock)}
        />
      )}

      {/* Add Stock Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Add Stock Item</h3>
              <button onClick={() => setShowAddForm(false)} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="block text-sm font-medium mb-1">Name *</label><input type="text" value={addForm.name} onChange={(e) => setAddForm(p => ({...p, name: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Paracetamol 500mg" /></div>
              <div><label className="block text-sm font-medium mb-1">Quantity</label><input type="number" value={addForm.quantity} onChange={(e) => setAddForm(p => ({...p, quantity: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Min Quantity</label><input type="number" value={addForm.minQuantity} onChange={(e) => setAddForm(p => ({...p, minQuantity: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Unit</label><input type="text" value={addForm.unit} onChange={(e) => setAddForm(p => ({...p, unit: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Supplier</label><input type="text" value={addForm.supplier} onChange={(e) => setAddForm(p => ({...p, supplier: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Batch Number</label><input type="text" value={addForm.batchNumber} onChange={(e) => setAddForm(p => ({...p, batchNumber: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Expiry Date</label><input type="date" value={addForm.expiryDate} onChange={(e) => setAddForm(p => ({...p, expiryDate: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Location</label><input type="text" value={addForm.location} onChange={(e) => setAddForm(p => ({...p, location: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Unit Cost ($)</label><input type="number" step="0.01" value={addForm.unitCost} onChange={(e) => setAddForm(p => ({...p, unitCost: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
            </div>
            <div className="flex justify-end gap-3 mt-4 pt-3 border-t">
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleAddStock} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"><Save className="w-4 h-4" /> Add Stock</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Stock Modal */}
      {showEditForm && editItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Edit Stock: {editItem.medication || editItem.name}</h3>
              <button onClick={() => setShowEditForm(false)} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium mb-1">Current Stock</label><input type="number" value={editItem.currentStock ?? editItem.quantity ?? ''} onChange={(e) => setEditItem(p => ({...p, currentStock: e.target.value, quantity: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Min Stock</label><input type="number" value={editItem.minStock ?? editItem.minQuantity ?? ''} onChange={(e) => setEditItem(p => ({...p, minStock: e.target.value, minQuantity: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Supplier</label><input type="text" value={editItem.supplier || ''} onChange={(e) => setEditItem(p => ({...p, supplier: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Batch</label><input type="text" value={editItem.batch || editItem.batchNumber || ''} onChange={(e) => setEditItem(p => ({...p, batch: e.target.value, batchNumber: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Location</label><input type="text" value={editItem.location || ''} onChange={(e) => setEditItem(p => ({...p, location: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Unit Cost ($)</label><input type="number" step="0.01" value={editItem.costPerUnit ?? editItem.unitCost ?? ''} onChange={(e) => setEditItem(p => ({...p, costPerUnit: e.target.value, unitCost: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
            </div>
            <div className="flex justify-between mt-4 pt-3 border-t">
              <button onClick={() => handleDeleteStock(editItem.id)} className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50">Delete</button>
              <div className="flex gap-3">
                <button onClick={() => setShowEditForm(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleEditStock} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StockDetailsModal = ({ item, onClose, onEdit }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Package className="w-6 h-6 text-blue-600" />
            Stock Details
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-lg mb-4">Stock Information</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Medication:</span>
                  <span className="font-medium">{item.medication}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Stock:</span>
                  <span className="font-medium">{item.currentStock} {item.unit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Min Stock:</span>
                  <span className="font-medium">{item.minStock} {item.unit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Max Stock:</span>
                  <span className="font-medium">{item.maxStock} {item.unit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Reorder Point:</span>
                  <span className="font-medium">{item.reorderPoint} {item.unit}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4">Batch Information</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Batch Number:</span>
                  <span className="font-medium">{item.batch}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Expiry Date:</span>
                  <span className="font-medium">{item.expiry}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Days to Expiry:</span>
                  <span className="font-medium">{item.daysToExpiry} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Supplier:</span>
                  <span className="font-medium">{item.supplier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium">{item.location}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold text-lg mb-4">Financial Information</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">${item.costPerUnit.toFixed(2)}</div>
                <div className="text-sm text-gray-600">Cost per Unit</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">${(item.currentStock * item.costPerUnit).toFixed(2)}</div>
                <div className="text-sm text-gray-600">Total Value</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{item.currentStock}</div>
                <div className="text-sm text-gray-600">Units in Stock</div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button 
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={onEdit}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Edit Stock
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockManagement;
